import type { AppAnnouncement } from '@/runtime/runtimeConfig';
import { useT } from '@/i18n/useI18n';

export function AnnouncementModal({
  announcement,
  onClose,
}: {
  announcement: AppAnnouncement;
  onClose: () => void;
}) {
  const t = useT();

  return (
    <div
      className="responsive-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-announcement-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 680,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding:
          'max(env(safe-area-inset-top, 0px), 18px) 18px max(env(safe-area-inset-bottom, 0px), 18px)',
        background: 'rgba(4,3,18,0.82)',
        backdropFilter: 'blur(7px)',
      }}
    >
      <div
        className="responsive-modal__card"
        style={{
          width: '100%',
          maxWidth: 380,
          maxHeight: 'min(78vh, 560px)',
          overflowY: 'auto',
          padding: 22,
          border: '1px solid rgba(34,211,238,0.38)',
          borderRadius: 18,
          background:
            'radial-gradient(circle at 50% 0%, rgba(155,93,229,0.22), transparent 55%), rgba(15,12,40,0.98)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.58), 0 0 28px rgba(34,211,238,0.13)',
          animation: 'cd-toast-in 0.25s ease-out',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 7,
            marginBottom: 16,
          }}
          aria-hidden="true"
        >
          {['var(--c-red)', 'var(--c-blue)', 'var(--c-yellow)', 'var(--c-purple)'].map(
            (color) => (
              <span
                key={color}
                style={{
                  width: 13,
                  height: 13,
                  borderRadius: '50%',
                  background: color,
                  boxShadow: `0 0 10px ${color}`,
                }}
              />
            ),
          )}
        </div>
        <div
          id="app-announcement-title"
          className="display"
          style={{
            color: 'var(--c-yellow)',
            fontSize: 20,
            lineHeight: 1.25,
            textAlign: 'center',
            overflowWrap: 'anywhere',
          }}
        >
          {announcement.title || t('app.announcement_title')}
        </div>
        <div
          style={{
            margin: '16px 0 20px',
            color: 'var(--ink)',
            fontSize: 14,
            lineHeight: 1.6,
            textAlign: 'center',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {announcement.message}
        </div>
        <button className="btn btn-primary" onClick={onClose} style={{ width: '100%' }}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

export function MaintenanceScreen({ message }: { message: string }) {
  const t = useT();

  return (
    <div
      className="screen scanlines"
      role="alert"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding:
          'max(env(safe-area-inset-top, 0px), 22px) 24px max(env(safe-area-inset-bottom, 0px), 22px)',
        overflow: 'hidden',
        background:
          'radial-gradient(ellipse at 50% 22%, rgba(244,199,36,0.18), transparent 48%), linear-gradient(180deg, var(--bg-mid), var(--bg-deep) 76%)',
      }}
    >
      <div className="hex-bg" />
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 390,
          textAlign: 'center',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 82,
            height: 82,
            margin: '0 auto 20px',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 24,
            border: '1px solid rgba(244,199,36,0.5)',
            background: 'rgba(244,199,36,0.1)',
            color: 'var(--c-yellow)',
            fontSize: 38,
            boxShadow: '0 0 34px rgba(244,199,36,0.16)',
          }}
        >
          🛠
        </div>
        <div
          className="display"
          style={{
            color: 'var(--c-yellow)',
            fontSize: 'clamp(25px, 7vw, 38px)',
            lineHeight: 1.1,
            textShadow: '0 0 20px rgba(244,199,36,0.35)',
          }}
        >
          {t('app.maintenance_title')}
        </div>
        <div
          style={{
            margin: '16px auto 18px',
            maxWidth: 330,
            color: 'var(--ink-muted)',
            fontSize: 14,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {message || t('app.maintenance_default')}
        </div>
      </div>
    </div>
  );
}
